import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { prefixZero } from '@/utils/number';

export const ONE_DAY = 24 * 3600 * 1000;

dayjs.extend(utc);

export const _dayjs = dayjs;

export const TIME_FORMAT = 'YYYY-MM-DD HH:mm:ss';
export const DATE_FORMAT = 'YYYY-MM-DD';

export const MINUTE_FORMAT = 'YYYY-MM-DD HH:mm';

export const MINUTE_FORMAT_WITHOUT_YEAR = 'MM-DD HH:mm';

// 默认只能选择的时间区间（6个月===181天）
export const DISABLED_DAYS = 181;

// 时间组件 默认值
export const RANGE_PICKER_DEFAULT = {
  defaultValueConstant: 7, // 默认展示天数7天
  disabledDateConstant: 6, // 默认限制时间6个月
};

/**
 * 格式化为utc展示时间
 *
 * @param {number|string} time 时间戳
 * @param {string} format 格式化方式
 * @return {string} utc时间展示
 */
export function formatUTCTime(time, format = TIME_FORMAT) {
  return `${dayjs.utc(Number(time)).format(format)}`;
}

/**
 *
 * 格式化为本地展示时间
 *
 * @param {number|string} time 时间戳
 * @param {string} format 格式化方式
 * @return {string} 本地时间展示
 */
export function formatLocalTime(time, format = TIME_FORMAT) {
  if (time == '0' || time == null) {
    return '--';
  }
  const timeStr = `${time}`.length > 10 ? time : `${time}000`;
  return `${dayjs.utc(Number(timeStr)).local().format(format)}`;
}

export const formatUTCDate = (time: any, format?: string) => {
  return dayjs
    .unix(+time)
    .utc()
    .format(format || TIME_FORMAT);
};

/**
 * 获取今天
 */
export const today = dayjs().endOf('day');

/**
 * 获取时间戳
 */
export function timestamp(date?: any, isStandard = false) {
  const time = dayjs(date).valueOf();
  return isStandard ? time / 1000 : time;
}

/**
 * 获取减去一定时间的日期
 */
export function timeSubtract(time, unit: any = 'day') {
  return dayjs().subtract(time, unit);
}

/**
 * 时间比较
 */
export function diffTimeNow(time) {
  const now = dayjs().valueOf();
  const end = dayjs(time).endOf('day').valueOf();
  return end > now ? now : end;
}

/**
 * 获取日期时间段
 */
export function rangeTime(days = 7) {
  return [dayjs().startOf('days').subtract(days, 'day'), dayjs().endOf('day')];
}

/**
 * 获取最近几天的时间戳
 *
 * @param {number} dayCount 天数
 * @param {boolean} exceptToday 是否去除今天
 * @return {Array} 最近几天时间范围，[startTimestamp, endTimestamp]
 */
export function getLastDaysRange(dayCount = 7, exceptToday = false) {
  const timeRange = [
    dayjs().startOf('day').subtract(dayCount, 'day'),
    dayjs().endOf('day'),
  ];

  if (exceptToday) {
    timeRange[1] = dayjs().endOf('day').subtract(1, 'day');
  }
  return timeRange;
}

/**
 *
 * 时间戳转单位时间
 *
 * @param {number|string} ms 时间戳
 * @return {string} 日、时、分、秒、毫秒、特殊小时（最大值99）
 */
export const msToRemainTime = (ms) => {
  if (!(typeof ms === 'number' && !Number.isNaN(ms))) {
    return {
      day: '00',
      hour: '00',
      min: '00',
      sec: '00',
      million: '000',
      extraHour: '00',
    };
  }
  const internalMS = Math.max(0, ms);
  const msPerSecond = 1000;
  const msPerMinute = msPerSecond * 60;
  const msPerHour = msPerMinute * 60;
  const msPerDay = msPerHour * 24;

  const day = Math.floor(internalMS / msPerDay);
  const hour = prefixZero(Math.floor((internalMS % msPerDay) / msPerHour), 2);
  const min = prefixZero(
    Math.floor(((internalMS % msPerDay) % msPerHour) / msPerMinute),
    2
  );
  const sec = prefixZero(
    Math.floor(
      (((internalMS % msPerDay) % msPerHour) % msPerMinute) / msPerSecond
    ),
    2
  );
  const million = prefixZero(internalMS % msPerSecond, 3);

  const extraHour = prefixZero(Math.min(day * 24 + parseInt(hour, 10), 99), 2);
  const doubleDigitDay = prefixZero(day, 2);

  return {
    day,
    hour,
    min,
    sec,
    million,
    extraHour,
    doubleDigitDay,
  };
};

/**
 * 05.24 16:37 => 05.24 17:00
 * 05.24 17:00 => 05.24 18:00
 * 05.24 23:45 => 05.24 24:00
 *
 * @param {*} timestamp 时间戳
 * @return {string} 如 05.24 24:00
 */
// eslint-disable-next-line
export const getNextClockTime = (timestamp) => {
  const utcTime = dayjs.utc(timestamp);
  const Md = utcTime.format('YYYY-MM-DD');
  const currentHour = utcTime.hour();
  let nextClock: string | number =
    Number(currentHour) < 24 ? currentHour + 1 : currentHour;
  nextClock = `${nextClock}`.length < 2 ? `0${nextClock}` : nextClock;
  return `${Md} ${nextClock}:00`;
};
/**
 * 判断time 在今天24点前发生，且在x 天以内
 *
 * @param {number | string | Date} time  时间
 * @param {number} xDays 多少天内
 * @return {boolean}
 */
export function happenInDays(time, xDays) {
  const onlineObj = dayjs(Number(time));
  const nowObj = dayjs();
  const endOfToday = dayjs(nowObj.format('YYYY-MM-DD 23:59:59'));
  const endOfXDayObj = dayjs(
    onlineObj.add(xDays, 'days').format('YYYY-MM-DD 23:59:59')
  );
  return onlineObj.isBefore(endOfToday) && endOfXDayObj.isAfter(nowObj);
}

/**
 * 倒计时
 *
 * @param {number} start 开始时间
 * @param {number} end 结束时间
 */
export const countdown = (start, end) => {
  let diff = end - start;

  diff = diff > 0 ? diff : 0;

  const d = parseInt(`${diff / (24 * 60 * 60 * 1000)}`, 10);
  const h = parseInt(`${(diff / (60 * 60 * 1000)) % 24}`, 10);
  const m = parseInt(`${(diff / (60 * 1000)) % 60}`, 10);
  const s = parseInt(`${(diff / 1000) % 60}`, 10);
  const ms = parseInt(`${diff % 1000}`, 10);

  return {
    d: `0${d}`.slice(-Math.max(`${d}`.length, 2)),
    h: `0${h}`.slice(-2),
    m: `0${m}`.slice(-2),
    s: `0${s}`.slice(-2),
    ms: `00${ms}`.slice(-3),
  };
};

/**
 * 获取 UTC offset
 *
 * @return {*}
 */
export function getTimezoneOffset() {
  const d = new Date();
  const offset = d.getTimezoneOffset();
  const sign = offset > 0 ? '-' : '+';
  const utcTime = Math.abs(offset) / 60;
  return `${sign}${utcTime}`;
}

/**
 * 转换时间
 * 时间选择器输入的值返回类型是dayjs的对象，需要取$d进行使用
 *
 * @param {number｜Object｜Date} value 时间类型
 */
export function convertDate(value) {
  if (typeof value === 'object') {
    if (value instanceof Date) {
      return value;
    }
    return value?.$d || value?._d;
  }
  return value;
}
